import React from 'react';
import { motion } from 'framer-motion';
import { Section } from '../components/ui/Section';
import { CTASection } from '../components/sections/CTASection';
import { Target, Eye, Compass } from 'lucide-react';
export function AboutPage() {
  return <main className="pt-24" data-id="element-241">
      {/* Hero */}
      <Section background="default" data-id="element-242">
        <div className="max-w-4xl" data-id="element-243">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-[#0C0C0C] mb-6 leading-tight" data-id="element-244">
            A Digital Partner Focused on Clarity, Quality, and Impact
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 leading-relaxed" data-id="element-245">
            We are a digital solutions and design-driven company dedicated to
            helping businesses and brands build meaningful, high-performing
            digital experiences.
          </motion.p>
        </div>
      </Section>

      {/* Story */}
      <Section background="white" data-id="element-246">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-247">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-248">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-6" data-id="element-249">
              Our Story
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-250">
              <p data-id="element-251">
                Our work blends strategy, creativity, and technology — ensuring
                everything we produce is not only visually refined, but
                purposeful and effective.
              </p>
              <p data-id="element-252">
                We believe great digital products are built at the intersection
                of usability, aesthetics, and business goals. Every project we
                take on is an opportunity to create something that truly
                matters.
              </p>
              <p data-id="element-253">
                From startups to established enterprises, we partner with
                organizations that value quality, clarity, and long-term
                thinking.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative h-[400px] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden" data-id="element-254">
            {/* Abstract visual */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-255">
              <div className="w-48 h-48 border-2 border-[#00316B]/20 rounded-full" data-id="element-256" />
              <div className="absolute w-32 h-32 bg-[#00316B]/10 rounded-lg transform rotate-12" data-id="element-257" />
              <div className="absolute w-24 h-24 bg-[#0C0C0C]/5 rounded-full translate-x-16 -translate-y-8" data-id="element-258" />
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Values */}
      <Section background="default" data-id="element-259">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-260">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-6" data-id="element-261">
            What Drives Us
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-262">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-xl border border-gray-100" data-id="element-263">
            <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mb-6" data-id="element-264">
              <Target className="w-6 h-6 text-[#00316B]" data-id="element-265" />
            </div>
            <h3 className="text-xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-266">
              Our Approach
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-267">
              Thoughtful, structured, and user-centered. We take the time to
              understand before we build.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-white p-8 rounded-xl border border-gray-100" data-id="element-268">
            <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mb-6" data-id="element-269">
              <Eye className="w-6 h-6 text-[#00316B]" data-id="element-270" />
            </div>
            <h3 className="text-xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-271">
              Our Focus
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-272">
              Clarity, simplicity, performance, and longevity. We build things
              that last.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white p-8 rounded-xl border border-gray-100" data-id="element-273">
            <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mb-6" data-id="element-274">
              <Compass className="w-6 h-6 text-[#00316B]" data-id="element-275" />
            </div>
            <h3 className="text-xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-276">
              Our Vision
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-277">
              To be the digital partner that businesses trust for quality,
              reliability, and results.
            </p>
          </motion.div>
        </div>
      </Section>

      <CTASection data-id="element-278" />
    </main>;
}